/**
 * Shared quote discount calculator.
 * Mirrors backend services/quote_pricing.py::calc_total_discount() exactly.
 *
 * Accepts:
 *   - quote.discounts: [{label, type: 'flat'|'percent', value}, ...] (preferred)
 *   - quote.discount_amount + quote.discount_label (legacy fallback)
 *
 * Returns: { totalRemoved, rows: [{ label, removed }] } so callers can both
 * subtract from a subtotal and render a per-line breakdown.
 */
export function computeQuoteDiscounts(subtotal, quote) {
  const sub = parseFloat(subtotal) || 0;
  if (!quote || sub <= 0) return { totalRemoved: 0, rows: [] };

  const list = Array.isArray(quote.discounts) ? quote.discounts.filter(d => d && parseFloat(d.value) > 0) : [];

  // Legacy single-discount fallback
  if (!list.length) {
    const legacyAmt = parseFloat(quote.discount_amount) || 0;
    if (legacyAmt <= 0) return { totalRemoved: 0, rows: [] };
    const removed = Math.min(legacyAmt, sub);
    return {
      totalRemoved: removed,
      rows: [{ label: quote.discount_label || "Discount", removed }],
    };
  }

  let running = sub;
  let totalRemoved = 0;
  const rows = [];
  for (const d of list) {
    if (running <= 0) break;
    const type = (d.type || "flat").toLowerCase();
    const val = parseFloat(d.value) || 0;
    if (val <= 0) continue;
    let removed = type === "percent" ? running * (val / 100) : val;
    removed = Math.min(removed, running);
    if (removed <= 0) continue;
    running -= removed;
    totalRemoved += removed;
    const labelBase = d.label || "Discount";
    const label = type === "percent" ? `${labelBase} (${val}%)` : labelBase;
    rows.push({ label, removed });
  }
  return { totalRemoved: Math.round(totalRemoved * 100) / 100, rows };
}
